// src/stores/adminAuthStore.js
import { defineStore } from "pinia";
import { urlAPI } from "./config";

export const useAdminAuthStore = defineStore("adminAuth", {
  state: () => ({
    // Настройки
    TOKEN_STORAGE_KEY: "admin_auth_token",
    USER_STORAGE_KEY: "admin_user_data",

    // Данные авторизации
    token: localStorage.getItem("admin_auth_token"),
    user: localStorage.getItem("admin_user_data"),

    // Состояния загрузки
    isLoading: false,
    isInitializing: false,

    // Обработка ошибок
    errors: {},
    message: "",
    lastOperationStatus: null, // 'success' | 'error' | null

    // Конфигурация
    config: {
      autoLogoutOn401: true,
      tokenRefreshEnabled: false,
      maxRetries: 3,
    },
  }),

  getters: {
    // Проверка авторизации
    isAuthenticated: (state) => {
      return !!state.token && !!state.user;
    },

    // Получение токена для запросов
    getAuthToken: (state) => {
      return state.token;
    },

    // Данные пользователя
    getCurrentUser: (state) => {
      return state.user;
    },

    // Проверка наличия ошибок
    hasErrors: (state) => {
      return Object.keys(state.errors).length > 0;
    },

    // Получение ошибок по полям
    getFieldErrors: (state) => (fieldName) => {
      return state.errors[fieldName] || [];
    },

    // Статус последней операции
    isLastOperationSuccess: (state) => {
      return state.lastOperationStatus === "success";
    },

    // Проверка состояния загрузки
    isProcessing: (state) => {
      return state.isLoading || state.isInitializing;
    },

    // Заголовки для API запросов
    getApiHeaders: (state) => {
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
      };

      if (state.token) {
        headers.Authorization = `Bearer ${state.token}`;
      }

      return headers;
    },
  },

  actions: {
    // Инициализация store - загрузка данных из localStorage
    init() {
      this.isInitializing = true;
      try {
        this.loadAuthData();
        console.log("Admin Auth Store инициализирован");
      } catch (error) {
        console.error("Ошибка инициализации Admin Auth Store:", error);
        this.clearAuthData();
      } finally {
        this.isInitializing = false;
      }
    },

    // Загрузка данных авторизации из localStorage
    loadAuthData() {
      try {
        const storedToken = localStorage.getItem(this.TOKEN_STORAGE_KEY);
        const storedUser = localStorage.getItem(this.USER_STORAGE_KEY);

        if (storedToken) {
          this.token = storedToken;
        }

        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser && typeof parsedUser === "object") {
            this.user = parsedUser;
          }
        }

        // Проверяем целостность данных
        if (this.token && !this.user) {
          console.warn("Найден токен без данных пользователя, очищаем");
          this.clearAuthData();
        }
      } catch (error) {
        console.error("Ошибка загрузки данных авторизации:", error);
        this.clearAuthData();
      }
    },

    // Сохранение данных авторизации в localStorage
    saveAuthData(token, user = null) {
      try {
        if (token) {
          this.token = token;
          localStorage.setItem(this.TOKEN_STORAGE_KEY, token);
        }

        if (user) {
          this.user = user;
          localStorage.setItem(this.USER_STORAGE_KEY, JSON.stringify(user));
        }

        console.log("Данные авторизации сохранены");
      } catch (error) {
        console.error("Ошибка сохранения данных авторизации:", error);
      }
    },

    // Очистка всех данных авторизации
    clearAuthData() {
      this.token = null;
      this.user = null;
      this.errors = {};
      this.message = "";
      this.lastOperationStatus = null;

      localStorage.removeItem(this.TOKEN_STORAGE_KEY);
      localStorage.removeItem(this.USER_STORAGE_KEY);

      console.log("Данные авторизации очищены");
    },

    // Очистка ошибок и сообщений
    clearMessages() {
      this.errors = {};
      this.message = "";
      this.lastOperationStatus = null;
    },

    // Универсальный метод для API запросов
    async makeApiRequest(endpoint, options = {}) {
      const url = `${urlAPI}${endpoint}`;
      const config = {
        method: "GET",
        headers: this.getApiHeaders,
        ...options,
      };

      try {
        const response = await fetch(url, config);
        const data = await response.json();

        if (!response.ok) {
          throw {
            status: response.status,
            data: data,
          };
        }

        return { success: true, data };
      } catch (error) {
        console.error(`API Error [${endpoint}]:`, error);

        if (error.status === 401 && this.config.autoLogoutOn401) {
          this.clearAuthData();
        }

        throw error;
      }
    },

    // Регистрация администратора
    async register(credentials) {
      this.isLoading = true;
      this.clearMessages();

      try {
        const { success, data } = await this.makeApiRequest("/admin/register", {
          method: "POST",
          body: JSON.stringify({
            username: credentials.username,
            password: credentials.password,
            password_confirmation: credentials.password_confirmation,
          }),
        });

        if (success && data.status === 201) {
          const user = { username: credentials.username };
          this.saveAuthData(data.data.token, user);
          this.message = data.message || "Регистрация успешна";
          this.lastOperationStatus = "success";

          return { success: true, data };
        }
      } catch (error) {
        this.handleApiError(error);
        return { success: false, error: error.data };
      } finally {
        this.isLoading = false;
      }
    },

    // Авторизация администратора
    async login(credentials) {
      this.isLoading = true;
      this.clearMessages();

      try {
        const { success, data } = await this.makeApiRequest("/admin/login", {
          method: "POST",
          body: JSON.stringify({
            username: credentials.username,
            password: credentials.password,
          }),
        });
        if (success && data.data.token) {
          const user = { username: credentials.username };
          this.saveAuthData(data.data.token, user);
          this.message = data.message || "Вход выполнен успешно";
          this.lastOperationStatus = "success";

          return { success: true, data };
        }
      } catch (error) {
        this.handleApiError(error);
        return { success: false, error: error.data };
      } finally {
        this.isLoading = false;
      }
    },

    // Выход из системы (текущая сессия)
    async logout() {
      this.isLoading = true;
      this.clearMessages();

      try {
        const { success, data } = await this.makeApiRequest(
          "/admin/logout",
          {
            method: "POST",
          }
        );

        if (success && data.success) {
          this.clearAuthData();
          this.message = data.message || "Выход выполнен успешно";
          this.lastOperationStatus = "success";

          return { success: true, data };
        }
      } catch (error) {
        // Даже при ошибке очищаем локальные данные
        this.clearAuthData();
        this.handleApiError(error);
        return { success: false, error: error.data };
      } finally {
        this.isLoading = false;
      }
    },

    // Завершение всех сессий
    async terminateAllSessions() {
      this.isLoading = true;
      this.clearMessages();

      try {
        const { success, data } = await this.makeApiRequest(
          "/admin/terminate",
          {
            method: "POST",
          }
        );

        if (success && data.success) {
          this.clearAuthData();
          this.message = data.message || "Все сессии завершены";
          this.lastOperationStatus = "success";

          return { success: true, data };
        }
      } catch (error) {
        // Даже при ошибке очищаем локальные данные
        this.clearAuthData();
        this.handleApiError(error);
        return { success: false, error: error.data };
      } finally {
        this.isLoading = false;
      }
    },

    // Смена пароля
    async changePassword(passwordData) {
      if (!this.isAuthenticated) {
        this.message = "Необходимо авторизоваться";
        this.lastOperationStatus = "error";
        return { success: false };
      }

      this.isLoading = true;
      this.clearMessages();

      try {
        const { success, data } = await this.makeApiRequest(
          "/admin/change_password",
          {
            method: "PATCH",
            body: JSON.stringify({
              current_password: passwordData.current_password,
              password: passwordData.password,
              password_confirmation: passwordData.password_confirmation,
            }),
          }
        );

        if (success && data.message === "OK") {
          this.message = data.message || "Пароль успешно изменен";
          this.lastOperationStatus = "success";

          return { success: true, data };
        }
      } catch (error) {
        this.handleApiError(error);
        return { success: false, error: error.data };
      } finally {
        this.isLoading = false;
      }
    },

    // Проверка действительности токена
    async checkAuthStatus() {
      if (!this.token) {
        return false;
      }

      try {
        const { success } = await this.makeApiRequest("/user");
        return success && this.isAuthenticated;
      } catch (error) {
        console.error("Ошибка проверки статуса авторизации:", error);
        this.clearAuthData();
        return false;
      }
    },

    // Обработка ошибок API
    handleApiError(error) {
      this.lastOperationStatus = "error";

      if (error.status) {
        const { status, data } = error;

        switch (status) {
          case 401:
            this.message = data?.message || "Ошибка авторизации";
            if (this.config.autoLogoutOn401) {
              this.clearAuthData();
            }
            break;

          case 422:
            this.errors = data?.errors || {};
            this.message = data?.message || "Ошибка валидации данных";
            break;

          case 404:
            this.message = "Запрашиваемый ресурс не найден";
            break;

          case 500:
            this.message = "Внутренняя ошибка сервера";
            break;

          default:
            this.message = data?.message || `Ошибка ${status}`;
        }
      } else {
        this.message = "Ошибка сети или соединения";
      }

      console.error("API Error handled:", {
        status: error.status,
        message: this.message,
        errors: this.errors,
      });
    },

    // Принудительное обновление данных пользователя
    async refreshUserData() {
      if (!this.isAuthenticated) {
        return false;
      }

      try {
        // Здесь можно добавить запрос для получения актуальных данных пользователя
        // const { success, data } = await this.makeApiRequest('/admin/profile')

        console.log("Данные пользователя обновлены");
        return true;
      } catch (error) {
        console.error("Ошибка обновления данных пользователя:", error);
        return false;
      }
    },

    // Сброс состояния store к начальным значениям
    resetStore() {
      this.clearAuthData();
      this.isLoading = false;
      this.isInitializing = false;
      console.log("Store сброшен к начальным значениям");
    },
  },
});

// В компоненте
/* const authStore = useAdminAuthStore()
authStore.init() // при запуске приложения
await authStore.login({ username, password }) */

/* 🔧 Основные особенности:
State включает:

🔐 token, user - данные авторизации
⏳ isLoading, isInitializing - состояния загрузки
❌ errors, message, lastOperationStatus - обработка ошибок
⚙️ Конфигурация API и localStorage ключи

Getters для удобного доступа:

✅ isAuthenticated - проверка авторизации
👤 getCurrentUser - данные пользователя
🚨 hasErrors, getFieldErrors - работа с ошибками
📋 getApiHeaders - заголовки для запросов

Actions покрывают весь API:

🚀 init() - инициализация из localStorage (как в вашем примере)
📝 register(), login() - регистрация и вход
🚪 logout(), terminateAllSessions() - выход
🔒 changePassword() - смена пароля
🔄 makeApiRequest() - универсальный метод для API
🧹 clearAuthData(), clearMessages() - очистка данных

Полная совместимость с API:

✅ Все эндпоинты из документации
🛡️ Автоматическая обработка ошибок 401/422
💾 Сохранение в localStorage
🔄 Автоочистка при ошибках авторизации
 */
